import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Calendar, ChevronUp, History, Loader2, Lock, Paperclip, Send, Smile, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePlatformDesignerBuyerPreview } from "@/lib/usePlatformDesignerBuyerPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  getWorkspacePresenceRoleLabel,
} from "@/lib/workspacePresence";
import WorkspaceOnlineBadges from "@/components/equipo/WorkspaceOnlineBadges";
import { isWorkspaceUserActive } from "@/lib/workspaceTeamLimits";
import { DESPACHO_TEAM_CHAT_SUBTITLE } from "@/modules/despacho/despachoChatUi";
import { workspaceUserDisplayName } from "@/modules/despacho/despachoWorkspaceAuxiliaries";
import {
  WORKSPACE_CHAT_DEFAULT_LIMIT,
  WORKSPACE_CHAT_EMOJIS,
  WORKSPACE_CHAT_POLL_MS,
  WORKSPACE_CHAT_RECENT_HINT,
  WORKSPACE_CHAT_EMBEDDED_LIST_CLASS,
  WORKSPACE_CHAT_ASISTENTE_LIST_CLASS,
  WORKSPACE_CHAT_FULLPAGE_LIST_CLASS,
  WORKSPACE_CHAT_ATTACHMENT_ACCEPT,
  WORKSPACE_CHAT_MAX_ATTACHMENTS_PER_MESSAGE,
  buildWorkspaceChatListParams,
  mergeWorkspaceChatMessages,
  validateWorkspaceChatAttachmentFile,
} from "@/lib/workspaceChat";

function formatChatTimestamp(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Chat interno del equipo — grupal o privado 1:1 vía /api/workspace/chat/messages.
 * @param {boolean} pinComposer — solo BNT Asistente: lista flexible + composer siempre visible abajo.
 */
export default function WorkspaceTeamChatBlock({
  embedded = false,
  activeParticipantCount = 0,
  pinComposer = false,
  initialPrivateRecipientId = "",
  highlightPrivateToggle = false,
}) {
  const { user } = useAuth();
  const buyerPreviewActive = usePlatformDesignerBuyerPreview();
  const readOnly = buyerPreviewActive;

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [recentWindow, setRecentWindow] = useState(true);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [chatMode, setChatMode] = useState("group");
  const [privateRecipientId, setPrivateRecipientId] = useState("");
  const [teamUsers, setTeamUsers] = useState([]);
  const [loadingTeamUsers, setLoadingTeamUsers] = useState(false);
  const [presenceItems, setPresenceItems] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const listRef = useRef(null);
  const composerRef = useRef(null);
  const fileInputRef = useRef(null);
  const skipAutoScrollRef = useRef(false);
  const historyModeRef = useRef(false);
  const messagesRef = useRef([]);
  const chatScopeRef = useRef({ chatMode: "group", privateRecipientId: "" });

  const isPrivateMode = chatMode === "private";
  const privateReady = !isPrivateMode || Boolean(privateRecipientId);

  const presenceById = useMemo(() => {
    const map = new Map();
    presenceItems.forEach((row) => {
      if (row?.id) map.set(row.id, row);
    });
    return map;
  }, [presenceItems]);

  const participantOptions = useMemo(
    () => (teamUsers || [])
      .filter((row) => row?.id && row.id !== user?.id && isWorkspaceUserActive(row))
      .map((row) => ({
        ...row,
        is_online: Boolean(presenceById.get(row.id)?.is_online),
      }))
      .sort((a, b) => {
        if (a.is_admin_owner && !b.is_admin_owner) return -1;
        if (!a.is_admin_owner && b.is_admin_owner) return 1;
        if (a.is_buyer_tenant && !b.is_buyer_tenant) return 1;
        if (!a.is_buyer_tenant && b.is_buyer_tenant) return -1;
        return workspaceUserDisplayName(a).localeCompare(workspaceUserDisplayName(b), "es");
      }),
    [teamUsers, user?.id, presenceById],
  );

  const onlineMembers = useMemo(() => {
    const byId = new Map((teamUsers || []).map((row) => [row.id, row]));
    return (presenceItems || [])
      .filter((row) => row?.is_online && row?.id)
      .map((row) => {
        const team = byId.get(row.id);
        return {
          ...row,
          label: team ? workspaceUserDisplayName(team) : row.name || row.email || "Usuario",
        };
      });
  }, [presenceItems, teamUsers]);

  const buildScopeParams = useCallback((extra = {}) => {
    return buildWorkspaceChatListParams({
      ...extra,
      channel: isPrivateMode ? "direct" : "group",
      recipientUserId: isPrivateMode ? privateRecipientId : "",
    });
  }, [isPrivateMode, privateRecipientId]);

  useEffect(() => {
    chatScopeRef.current = { chatMode, privateRecipientId };
  }, [chatMode, privateRecipientId]);

  useEffect(() => {
    if (!initialPrivateRecipientId) return;
    setChatMode("private");
    setPrivateRecipientId(initialPrivateRecipientId);
  }, [initialPrivateRecipientId]);

  useEffect(() => {
    if (readOnly) return undefined;
    let cancelled = false;
    const loadTeamDirectory = async () => {
      setLoadingTeamUsers(true);
      try {
        const [usersRes, presenceRes] = await Promise.all([
          api.get("/admin/users").catch(() => ({ data: [] })),
          api.get("/workspace/chat/presence").catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        setTeamUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setPresenceItems(Array.isArray(presenceRes.data?.items) ? presenceRes.data.items : []);
      } catch {
        if (!cancelled) {
          setTeamUsers([]);
          setPresenceItems([]);
        }
      } finally {
        if (!cancelled) setLoadingTeamUsers(false);
      }
    };
    loadTeamDirectory();
    const timer = window.setInterval(loadTeamDirectory, WORKSPACE_CHAT_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [readOnly]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const fetchMessages = useCallback(async (params, { appendPoll = false } = {}) => {
    const { data } = await api.get("/workspace/chat/messages", { params });
    const items = Array.isArray(data?.items) ? data.items : [];
    setHasMore(Boolean(data?.has_more));
    setRecentWindow(!params.include_older && !params.date_from && !params.date_to && !params.before);
    if (appendPoll && params.after) {
      setMessages((prev) => mergeWorkspaceChatMessages(prev, items));
    } else {
      setMessages(items);
    }
    return data;
  }, []);

  const loadRecent = useCallback(async ({ silent = false } = {}) => {
    if (!privateReady) {
      if (!silent) {
        setMessages([]);
        setLoading(false);
      }
      return;
    }
    if (!silent) setError("");
    try {
      const current = messagesRef.current;
      const last = current[current.length - 1];
      const params = buildScopeParams(
        last?.created_at && silent
          ? { after: last.created_at, limit: WORKSPACE_CHAT_DEFAULT_LIMIT }
          : {},
      );
      await fetchMessages(params, { appendPoll: Boolean(silent && last?.created_at) });
    } catch (err) {
      const status = err?.response?.status;
      if (readOnly && status === 403) {
        setMessages([]);
        setError("");
      } else if (!silent) {
        setMessages([]);
        setError(
          err?.response?.data?.detail
            || "No se pudo cargar el chat interno. Intente de nuevo.",
        );
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [buildScopeParams, fetchMessages, privateReady, readOnly]);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    historyModeRef.current = false;
    loadRecent();
  }, [chatMode, privateRecipientId, loadRecent]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!historyModeRef.current) {
        loadRecent({ silent: true });
      }
    }, WORKSPACE_CHAT_POLL_MS);
    return () => clearInterval(timer);
  }, [loadRecent]);

  useEffect(() => {
    if (!loading && !skipAutoScrollRef.current) {
      scrollToBottom();
    }
    skipAutoScrollRef.current = false;
  }, [loading, messages, scrollToBottom]);

  const handleLoadMore = async () => {
    const oldest = messages[0];
    if (!oldest?.created_at || loadingMore) return;
    setLoadingMore(true);
    setError("");
    skipAutoScrollRef.current = true;
    const prevHeight = listRef.current?.scrollHeight || 0;
    try {
      const params = buildScopeParams({
        before: oldest.created_at,
        includeOlder: true,
      });
      historyModeRef.current = true;
      const { data } = await api.get("/workspace/chat/messages", { params });
      const items = Array.isArray(data?.items) ? data.items : [];
      setHasMore(Boolean(data?.has_more));
      setRecentWindow(false);
      setMessages((prev) => mergeWorkspaceChatMessages(items, prev));
      requestAnimationFrame(() => {
        const el = listRef.current;
        if (el) {
          el.scrollTop = el.scrollHeight - prevHeight;
        }
      });
    } catch (err) {
      setError(
        err?.response?.data?.detail
          || "No se pudo cargar mensajes anteriores.",
      );
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLoadHistory = async () => {
    setLoading(true);
    setError("");
    historyModeRef.current = true;
    try {
      await fetchMessages(buildScopeParams({ includeOlder: true }));
    } catch (err) {
      setError(
        err?.response?.data?.detail
          || "No se pudo cargar el historial.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateFilter = async () => {
    if (!dateFrom && !dateTo) return;
    setLoading(true);
    setError("");
    historyModeRef.current = true;
    try {
      await fetchMessages(
        buildScopeParams({
          dateFrom,
          dateTo,
          includeOlder: true,
          limit: WORKSPACE_CHAT_DEFAULT_LIMIT,
        }),
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail
          || "No se pudo filtrar por fecha.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetToRecent = async () => {
    setDateFrom("");
    setDateTo("");
    setShowDateFilter(false);
    historyModeRef.current = false;
    setLoading(true);
    setError("");
    try {
      await fetchMessages(buildScopeParams({}));
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo recargar mensajes recientes.");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (readOnly || !privateReady) return;
    const text = composer.trim();
    const attachmentIds = pendingAttachments.map((a) => a.id).filter(Boolean);
    if ((!text && attachmentIds.length === 0) || sending || uploadingAttachment) return;
    setSending(true);
    setError("");
    try {
      const payload = {
        body: text,
        attachment_ids: attachmentIds,
      };
      if (isPrivateMode) {
        payload.recipient_user_id = privateRecipientId;
      }
      const { data } = await api.post("/workspace/chat/messages", payload);
      setMessages((prev) => mergeWorkspaceChatMessages(prev, [data]));
      setComposer("");
      setPendingAttachments([]);
      setShowEmoji(false);
      scrollToBottom();
    } catch (err) {
      setError(
        err?.response?.data?.detail
          || "No se pudo enviar el mensaje. Verifique su conexión e intente de nuevo.",
      );
    } finally {
      setSending(false);
    }
  };

  const handleAttachmentPick = () => {
    if (readOnly || loading || sending || uploadingAttachment) return;
    fileInputRef.current?.click();
  };

  const handleAttachmentSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || readOnly) return;
    if (pendingAttachments.length >= WORKSPACE_CHAT_MAX_ATTACHMENTS_PER_MESSAGE) {
      setError(`Máximo ${WORKSPACE_CHAT_MAX_ATTACHMENTS_PER_MESSAGE} adjuntos por mensaje.`);
      return;
    }
    const validationError = validateWorkspaceChatAttachmentFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setUploadingAttachment(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/workspace/chat/attachments", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.id) {
        setPendingAttachments((prev) => [...prev, {
          id: data.id,
          name: data.name || data.filename || file.name,
        }]);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail
          || "No se pudo subir el adjunto. Intente de nuevo.",
      );
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removePendingAttachment = (attachmentId) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  };

  const downloadAttachment = async (attachmentId, filename) => {
    try {
      const { data } = await api.get(`/workspace/chat/attachments/${attachmentId}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename || "adjunto";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err?.response?.data?.detail
          || "No se pudo descargar el adjunto.",
      );
    }
  };

  const appendEmoji = (emoji) => {
    setComposer((prev) => `${prev}${emoji}`);
    composerRef.current?.focus();
  };

  const handleTogglePrivateMode = () => {
    setShowDateFilter(false);
    setChatMode((prev) => {
      if (prev === "private") return "group";
      if (!privateRecipientId && participantOptions.length === 1) {
        setPrivateRecipientId(participantOptions[0].id);
      }
      return "private";
    });
  };

  const selectedPrivateRecipient = participantOptions.find((row) => row.id === privateRecipientId);
  const privateRecipientLabel = selectedPrivateRecipient
    ? workspaceUserDisplayName(selectedPrivateRecipient)
    : "Destinatario";

  const privateRecipientPicker = isPrivateMode ? (
    <div
      className="rounded-md border border-slate-200 bg-slate-50 p-2 space-y-1.5"
      data-testid="workspace-team-chat-private-picker"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-semibold text-[#0F2D5C] shrink-0">Para:</span>
        {loadingTeamUsers ? (
          <span className="text-[10px] text-slate-500 inline-flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
            Cargando equipo…
          </span>
        ) : participantOptions.length === 0 ? (
          <span className="text-[10px] text-slate-500">No hay otros miembros en el equipo.</span>
        ) : (
          participantOptions.map((row) => {
            const label = workspaceUserDisplayName(row);
            const role = getWorkspacePresenceRoleLabel(row);
            const selected = row.id === privateRecipientId;
            return (
              <button
                key={row.id}
                type="button"
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] border transition-colors ${
                  selected
                    ? "bg-[#0F2D5C] text-white border-[#0F2D5C]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-[#0F2D5C]/40"
                }`}
                onClick={() => setPrivateRecipientId(row.id)}
                aria-pressed={selected}
                data-testid={`workspace-team-chat-recipient-${row.id}`}
              >
                {row.is_online ? (
                  <span className={`h-1.5 w-1.5 rounded-full ${selected ? "bg-emerald-300" : "bg-emerald-500"}`} aria-hidden />
                ) : null}
                <span>{label}</span>
                <span className={selected ? "text-white/75" : "text-slate-400"}>({role})</span>
              </button>
            );
          })
        )}
      </div>
      {!privateRecipientId && participantOptions.length > 0 ? (
        <p className="text-[10px] text-amber-700 m-0">Seleccione a quién enviar el mensaje privado.</p>
      ) : null}
      {privateRecipientId ? (
        <p className="text-[10px] text-slate-500 m-0">
          Solo usted y <strong>{privateRecipientLabel}</strong> verán esta conversación.
        </p>
      ) : null}
    </div>
  ) : null;

  const participantLabel = isPrivateMode
    ? (privateRecipientId ? `Privado con ${privateRecipientLabel}` : "Seleccione destinatario")
    : loadingTeamUsers
      ? "Consultando presencia…"
      : onlineMembers.length > 0
        ? `${onlineMembers.length} en línea`
        : "Sin nadie en línea";

  const participantBadge = (
    <span className="inline-flex items-center gap-1 text-[10px] text-slate-600 shrink-0">
      <Users className="w-3 h-3" aria-hidden />
      {participantLabel}
    </span>
  );

  const groupOnlineStrip = !isPrivateMode && !readOnly && !pinComposer ? (
    <WorkspaceOnlineBadges
      presenceItems={presenceItems}
      teamUsers={teamUsers}
      currentUserId={user?.id}
      loading={loadingTeamUsers}
      testIdPrefix="workspace-team-chat-group-online"
      emptyLabel="Nadie conectado en este momento."
    />
  ) : null;

  const historyControls = (
    <div className={embedded ? "space-y-1" : "space-y-2"} data-testid="workspace-team-chat-history-controls">
      {!embedded && recentWindow ? (
        <p className="text-[10px] text-slate-500 m-0 rounded-sm bg-slate-50 border border-slate-100 px-2 py-1.5">
          {WORKSPACE_CHAT_RECENT_HINT}
        </p>
      ) : null}
      {!embedded && !recentWindow ? (
        <p className="text-[10px] text-amber-800 m-0 rounded-sm bg-amber-50 border border-amber-100 px-2 py-1.5">
          Viendo historial ampliado. Los mensajes antiguos permanecen almacenados.
        </p>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {hasMore ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[10px]"
            onClick={handleLoadMore}
            disabled={loading || loadingMore}
            data-testid="workspace-team-chat-load-more"
          >
            {loadingMore ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : (
              <ChevronUp className="w-3 h-3 mr-1" />
            )}
            Cargar más mensajes
          </Button>
        ) : null}
        {recentWindow ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[10px]"
            onClick={handleLoadHistory}
            disabled={loading || loadingMore}
            data-testid="workspace-team-chat-load-history"
          >
            <History className="w-3 h-3 mr-1" />
            Cargar historial
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[10px]"
            onClick={handleResetToRecent}
            disabled={loading || loadingMore}
            data-testid="workspace-team-chat-back-recent"
          >
            Volver a recientes
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-[10px]"
          onClick={() => setShowDateFilter((v) => !v)}
          data-testid="workspace-team-chat-date-toggle"
        >
          <Calendar className="w-3 h-3 mr-1" />
          Filtrar por fecha
        </Button>
        <Button
          type="button"
          variant={isPrivateMode ? "default" : "ghost"}
          size="sm"
          className={`h-7 text-[10px] ${isPrivateMode ? "bg-[#0F2D5C] hover:bg-[#0a2248]" : ""} ${
            highlightPrivateToggle ? "ring-2 ring-rose-500 animate-pulse" : ""
          }`}
          onClick={handleTogglePrivateMode}
          data-testid="workspace-team-chat-private-toggle"
        >
          <Lock className="w-3 h-3 mr-1" />
          Privado
        </Button>
      </div>
      {showDateFilter ? (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border border-slate-200 bg-white p-2"
          data-testid="workspace-team-chat-date-filter"
        >
          <div>
            <Label className="text-[10px] text-slate-600">Desde</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="mt-0.5 h-8 text-xs"
              data-testid="workspace-team-chat-date-from"
            />
          </div>
          <div>
            <Label className="text-[10px] text-slate-600">Hasta</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="mt-0.5 h-8 text-xs"
              data-testid="workspace-team-chat-date-to"
            />
          </div>
          <div className="sm:col-span-2 flex gap-2">
            <Button
              type="button"
              size="sm"
              className="h-7 text-[10px] bg-[#0F2D5C] hover:bg-[#0a2248]"
              onClick={handleApplyDateFilter}
              disabled={loading || (!dateFrom && !dateTo)}
              data-testid="workspace-team-chat-date-apply"
            >
              Aplicar filtro
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-[10px]"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
            >
              Limpiar
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );

  const listHeightClass = !embedded
    ? WORKSPACE_CHAT_FULLPAGE_LIST_CLASS
    : pinComposer
      ? WORKSPACE_CHAT_ASISTENTE_LIST_CLASS
      : WORKSPACE_CHAT_EMBEDDED_LIST_CLASS;

  const messageList = (
    <ul
      ref={listRef}
      className={`space-y-3 m-0 p-2 list-none overflow-y-auto bg-slate-100/60 rounded-lg ${listHeightClass}`}
      data-testid="workspace-team-chat-messages"
    >
      {loading ? (
        <li className="py-4 text-center text-xs text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin mx-auto" aria-hidden />
        </li>
      ) : null}
      {!loading && messages.length === 0 ? (
        <li
          className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-4 text-xs text-slate-600 text-center"
          data-testid="workspace-team-chat-empty"
        >
          {isPrivateMode
            ? (privateRecipientId
              ? `Aún no hay mensajes privados con ${privateRecipientLabel}.`
              : "Seleccione una persona para iniciar un chat privado.")
            : "Aún no hay mensajes. Envía el primer mensaje al equipo."}
        </li>
      ) : null}
      {!loading
        ? messages.map((msg) => {
            const isMine = msg.sender_user_id && msg.sender_user_id === user?.id;
            return (
              <li
                key={msg.id}
                className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                data-testid={`workspace-team-chat-msg-${msg.id}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs shadow-sm ${
                    isMine
                      ? "bg-[#dcf8c6] text-slate-900 rounded-br-sm"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                  }`}
                >
                  <div className="flex flex-wrap items-baseline gap-2 mb-0.5">
                    <span className={`font-semibold ${isMine ? "text-emerald-900" : "text-[#0F2D5C]"}`}>
                      {msg.sender_name || "Usuario"}
                      {isMine ? " (tú)" : ""}
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {formatChatTimestamp(msg.created_at)}
                    </span>
                  </div>
                  <p className="m-0 leading-relaxed whitespace-pre-wrap break-words">{msg.body}</p>
                  {Array.isArray(msg.attachments) && msg.attachments.length > 0 ? (
                    <ul className="mt-1 m-0 p-0 list-none space-y-1">
                      {msg.attachments.map((att) => (
                        <li key={att.id || att.name}>
                          <button
                            type="button"
                            className="text-[10px] text-[#0F2D5C] underline hover:text-[#0a2248]"
                            onClick={() => downloadAttachment(att.id, att.name || att.filename)}
                            data-testid={`workspace-team-chat-attachment-${att.id}`}
                          >
                            📎 {att.name || att.filename || "Adjunto"}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </li>
            );
          })
        : null}
    </ul>
  );

  const composerBlock = readOnly ? (
    <p
      className="text-[10px] text-amber-800 m-0 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2"
      data-testid="workspace-team-chat-preview-readonly"
    >
      Vista previa del comprador — el chat interno es solo lectura; no se envían mensajes reales.
    </p>
  ) : (
    <div className="space-y-2">
      {privateRecipientPicker}
      {showEmoji ? (
        <div
          className="flex flex-wrap gap-1 rounded-md border border-slate-200 bg-white p-2"
          data-testid="workspace-team-chat-emoji-bar"
        >
          {WORKSPACE_CHAT_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="text-lg leading-none p-1 rounded hover:bg-slate-100"
              onClick={() => appendEmoji(emoji)}
              aria-label={`Insertar ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      ) : null}
      {pendingAttachments.length > 0 ? (
        <ul
          className="flex flex-wrap gap-2 m-0 p-0 list-none"
          data-testid="workspace-team-chat-pending-attachments"
        >
          {pendingAttachments.map((att) => (
            <li
              key={att.id}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] text-slate-700"
            >
              <Paperclip className="w-3 h-3 shrink-0" aria-hidden />
              <span className="max-w-[10rem] truncate">{att.name}</span>
              <button
                type="button"
                className="text-slate-400 hover:text-red-600 leading-none"
                onClick={() => removePendingAttachment(att.id)}
                aria-label={`Quitar ${att.name}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={WORKSPACE_CHAT_ATTACHMENT_ACCEPT}
        onChange={handleAttachmentSelected}
        data-testid="workspace-team-chat-attach-input"
      />
      <div className="flex gap-2 items-end">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full"
          onClick={() => setShowEmoji((v) => !v)}
          data-testid="workspace-team-chat-emoji-toggle"
          aria-label="Emojis"
          disabled={loading || sending || uploadingAttachment}
        >
          <Smile className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full"
          onClick={handleAttachmentPick}
          disabled={loading || sending || uploadingAttachment}
          title="Adjuntar archivo (PDF, XML, imágenes, Office — máx. 10 MB)"
          data-testid="workspace-team-chat-attach"
          aria-label="Adjuntar archivo"
        >
          {uploadingAttachment ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
          ) : (
            <Paperclip className="w-4 h-4" />
          )}
        </Button>
        <SpellcheckTextarea
          ref={composerRef}
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          placeholder={isPrivateMode
            ? (privateRecipientId
              ? `Mensaje privado para ${privateRecipientLabel}…`
              : "Seleccione destinatario arriba…")
            : "Escribe un mensaje al equipo…"}
          wrapperClassName="flex-1 min-w-0 space-y-1"
          className="min-h-[2.25rem] max-h-32 resize-y rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#0F2D5C]/20"
          testId="workspace-team-chat-composer"
          enableSpellcheck
          showSuggestionBar
          disabled={loading || sending || uploadingAttachment || !privateReady}
          rows={1}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          type="button"
          size="icon"
          className="rounded-full h-9 w-9 shrink-0 bg-[#0F2D5C] hover:bg-[#0a2248]"
          onClick={handleSend}
          disabled={loading || sending || uploadingAttachment || !privateReady || (!composer.trim() && pendingAttachments.length === 0)}
          data-testid="workspace-team-chat-send"
          aria-label="Enviar"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
      {!pinComposer ? (
        <p className="text-[10px] text-slate-400 m-0">Enter envía · Shift+Enter nueva línea</p>
      ) : null}
    </div>
  );

  const errorBlock = error ? (
    <div
      className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
      data-testid="workspace-team-chat-error"
    >
      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden />
      <span>{typeof error === "string" ? error : "Error en el chat interno."}</span>
    </div>
  ) : null;

  const body = (
    <>
      {!embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] text-slate-500 m-0">{DESPACHO_TEAM_CHAT_SUBTITLE}</p>
          {participantBadge}
        </div>
      ) : null}

      {errorBlock}

      {historyControls}
      {groupOnlineStrip}
      {messageList}
      {composerBlock}

      {!embedded ? (
        <p className="text-[10px] text-slate-500 m-0 border-l-2 border-slate-200 pl-2">
          Canal interno del equipo. No use este chat para datos sensibles de clientes ni WhatsApp externo.
        </p>
      ) : null}
    </>
  );

  if (embedded && pinComposer) {
    return (
      <section
        className="flex flex-col h-full min-h-0 gap-2"
        data-testid="workspace-team-chat-block"
        data-pin-composer="1"
      >
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 shrink-0">
          <h3 className="text-xs font-bold text-[#0F2D5C] m-0">Chat interno del equipo</h3>
          {participantBadge}
        </div>
        {errorBlock ? <div className="shrink-0">{errorBlock}</div> : null}
        <div className="shrink-0">{historyControls}</div>
        {groupOnlineStrip ? <div className="shrink-0">{groupOnlineStrip}</div> : null}
        {messageList}
        <div className="shrink-0 border-t border-slate-200 pt-2 bg-[#F4F6F8]">{composerBlock}</div>
      </section>
    );
  }

  if (embedded) {
    return (
      <section className="space-y-2" data-testid="workspace-team-chat-block">
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <h3 className="text-xs font-bold text-[#0F2D5C] m-0">Chat interno del equipo</h3>
          {participantBadge}
        </div>
        {body}
      </section>
    );
  }

  return (
    <DespachoSectionCard
      title="Chat interno del equipo"
      description={DESPACHO_TEAM_CHAT_SUBTITLE}
      testId="workspace-team-chat-block"
    >
      {body}
    </DespachoSectionCard>
  );
}
